import React, { useMemo } from 'react';
import { Grid } from '@mui/material';
import { StepWithResponseData } from 'hooks/activities/useQueryActivityAssignments';
import OverviewDetail from 'components/Overview/OverviewDetail/OverviewDetail';
import { QuestionChoice, QuestionType } from '__generated__/graphql';
import TextQuestion from 'pages/Activities/ActivityAssignmentStep/StepQuestion/TextQuestion/TextQuestion';
import ChoiceQuestion from 'pages/Activities/ActivityAssignmentStep/StepQuestion/ChoiceQuestion/ChoiceQuestion';
import { StepInterface } from 'pages/Activities/activities.context';
import validateQuestion from 'pages/Activities/ActivityAssignmentStep/StepQuestion/utils/validateQuestion';
import ValidationErrors from 'components/ValidationErrors/ValidationErrors';

function getQuestionAnswerValue(
  step: StepInterface,
  response?: StepWithResponseData['response']
): string | QuestionChoice[] | undefined {
  if (!response || !step.question) {
    return undefined;
  }

  switch (step.question.type) {
    case QuestionType.Choice:
      return (response.answers || [])
        .map((x) => x.selectedChoice)
        .filter((x): x is QuestionChoice => !!x);
    case QuestionType.Text:
      return response.textAnswer != null ? response.textAnswer : undefined;
    default:
      return undefined;
  }
}

interface StepQuestionViewProps {
  step: StepInterface;
  response?: StepWithResponseData['response'];
}

type Props = StepQuestionViewProps;

const StepQuestionView = ({ step, response }: Props) => {
  const question = step.question;
  const questionType = question?.type;
  const answerValue = useMemo(
    () => questionType && getQuestionAnswerValue(step, response),
    [questionType, response, step]
  );
  const validationErrors = useMemo(() => validateQuestion(question), [question]);

  if (!question) {
    return null;
  }

  return (
    <Grid container sx={{ marginTop: '3.4375rem' }}>
      <OverviewDetail title={question.questionDetail}>
        {questionType === QuestionType.Text && (
          <TextQuestion
            step={step}
            question={question}
            value={answerValue as string | undefined}
            readOnly
          />
        )}
        {questionType === QuestionType.Choice && (
          <ChoiceQuestion
            step={step}
            question={question}
            value={(answerValue || []) as QuestionChoice[]}
            readOnly
          />
        )}
        <ValidationErrors errors={validationErrors} />
      </OverviewDetail>
    </Grid>
  );
};

export default StepQuestionView;
